import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Calendar, User, Clock, Check } from "lucide-react";
import { Toaster } from "sonner";
import { getServiceBySlug, SERVICES } from "../data/services";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import CallNowButton from "../components/CallNowButton";

const easeExpo = [0.16, 1, 0.3, 1];

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });
}

function updateSeo(service) {
  document.title = service.seoTitle;
  const setMeta = (name, content, prop = false) => {
    const attr = prop ? "property" : "name";
    let el = document.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, name);
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
  };
  setMeta("description", service.seoDescription);
  setMeta("keywords", service.keywords);
  setMeta("og:title", service.seoTitle, true);
  setMeta("og:description", service.seoDescription, true);
  setMeta("og:type", "article", true);
  setMeta("og:url", `https://gmprint.com.au/${service.slug}`, true);

  // Article JSON-LD
  const scriptId = "article-jsonld";
  document.getElementById(scriptId)?.remove();
  const script = document.createElement("script");
  script.id = scriptId;
  script.type = "application/ld+json";
  script.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": service.articleTitle,
    "description": service.seoDescription,
    "author": { "@type": "Person", "name": service.author, "jobTitle": service.authorRole },
    "publisher": {
      "@type": "Organization",
      "name": "GM Print",
      "logo": { "@type": "ImageObject", "url": "https://gmprint.com.au/logos/gm-logo-light.png" }
    },
    "datePublished": service.publishedDate,
    "dateModified": service.publishedDate,
    "image": service.heroImage,
    "mainEntityOfPage": { "@type": "WebPage", "@id": `https://gmprint.com.au/${service.slug}` }
  });
  document.head.appendChild(script);
}

export default function ServicePage({ slugOverride }) {
  const params = useParams();
  const slug = slugOverride || params.slug;
  const service = getServiceBySlug(slug);

  useEffect(() => {
    if (service) {
      updateSeo(service);
      window.scrollTo(0, 0);
    }
    return () => {
      // Restore default title on unmount
      document.title = "Printing, Signage & Design Brisbane | GM Print";
    };
  }, [service]);

  if (!service) return <Navigate to="/" replace />;

  const related = service.relatedServices
    .map((s) => SERVICES.find((x) => x.slug === s))
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-white" data-testid="service-page">
      <Navigation />
      <CallNowButton />

      <main className="pt-28">
        {/* Breadcrumb */}
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-black/50 hover:text-black transition-colors" data-testid="service-back-link">
            <ArrowLeft size={14} /> Back to home
          </Link>
        </div>

        {/* Hero */}
        <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16 md:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className={service.heroImage ? "lg:col-span-8" : "lg:col-span-12"}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="w-14 h-px bg-black" />
                <span className="label-caps">{service.tileTitle} · Brisbane</span>
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: easeExpo }}
                className="font-display text-6xl md:text-7xl lg:text-8xl leading-[0.9] mb-6"
                data-testid="service-headline"
              >
                {service.heading}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1, ease: easeExpo }}
                className="font-body text-lg md:text-xl text-black/70 max-w-2xl mb-8"
              >
                {service.subheading}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2, ease: easeExpo }}
                className="flex flex-wrap gap-4"
              >
                <a href="/#contact" className="btn-primary" data-testid="service-cta-quote">
                  Get Your Free Quote <ArrowRight size={16} />
                </a>
              </motion.div>
            </div>
            {service.heroImage && (
              <motion.div
                initial={{ opacity: 0, scale: 1.02 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, delay: 0.2, ease: easeExpo }}
                className="lg:col-span-4"
              >
                <div className="service-hero-frame">
                  <img src={service.heroImage} alt={service.heroImageAlt} />
                  <div className="cmyk-bar cmyk-bar-thin absolute bottom-0 left-0 right-0">
                    <div className="c" /><div className="m" /><div className="y" /><div className="k" />
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </section>

        {/* Article */}
        <section className="bg-[#F5F5F5] border-y-2 border-black">
          <div className="max-w-[900px] mx-auto px-6 md:px-10 py-16 md:py-24">
            {/* Author meta */}
            <div className="flex flex-wrap items-center gap-6 mb-10 pb-8 border-b border-black/15">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-[#EC008C] flex items-center justify-center font-display text-lg text-black">SM</div>
                <div>
                  <div className="font-body text-sm font-semibold" data-testid="article-author">{service.author}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-black/50">{service.authorRole}</div>
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs font-mono text-black/50">
                <span className="flex items-center gap-1.5"><Calendar size={12} /> <span data-testid="article-published">{formatDate(service.publishedDate)}</span></span>
                <span className="flex items-center gap-1.5"><Clock size={12} /> {service.readTime}</span>
              </div>
            </div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: easeExpo }}
              className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] mb-10"
              data-testid="article-title"
            >
              {service.articleTitle}
            </motion.h2>

            <div className="space-y-6 mb-14">
              {service.articleBody.map((p, i) => (
                <p key={i} className="font-body text-lg leading-relaxed text-black/85">{p}</p>
              ))}
            </div>

            <div className="space-y-12">
              {service.articleSections.map((section, i) => (
                <motion.div
                  key={section.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.6, delay: i * 0.05, ease: easeExpo }}
                >
                  <div className="flex items-baseline gap-4 mb-3">
                    <span className="font-mono text-xs text-black/40">0{i + 1}</span>
                    <h3 className="font-display text-2xl md:text-3xl leading-tight">{section.title}</h3>
                  </div>
                  <p className="font-body text-base md:text-lg leading-relaxed text-black/70 ml-8">{section.body}</p>
                </motion.div>
              ))}
            </div>

            {/* Inline CTA */}
            <div className="mt-14 pt-10 border-t-2 border-black">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div>
                  <div className="font-display text-3xl md:text-4xl mb-1">Ready to price this up?</div>
                  <div className="flex items-center gap-2 text-sm text-black/60"><Check size={16} /><span className="font-mono text-xs">24hr response guaranteed</span></div>
                </div>
                <a href="/#contact" className="btn-primary" data-testid="service-cta-bottom">
                  Get Your Free Quote <ArrowRight size={16} />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Related */}
        <section className="bg-white">
          <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 md:py-24">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-14 h-px bg-black" />
              <span className="label-caps">Related Services</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl mb-10">What else we produce in Brisbane.</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((r, i) => (
                <Link
                  key={r.slug}
                  to={`/${r.slug}`}
                  className="brand-card group block"
                  data-testid={`related-${r.slug}`}
                >
                  <div className="font-mono text-xs text-black/40 mb-4">0{i + 1}</div>
                  <h3 className="font-display text-2xl mb-3">{r.tileTitle}</h3>
                  <p className="font-body text-sm text-black/70 leading-relaxed mb-5">{r.tileDescription}</p>
                  <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest">
                    Read more <ArrowRight size={14} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <Toaster position="bottom-right" richColors />
    </div>
  );
}
